import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSnackbar } from 'notistack';
import tenantEmailSettingsService, {
  TenantEmailSettingsStatus,
} from '../../services/tenantEmailSettingsService';
import {
  detectProviderFromEmail,
  detectProviderFromSmtpServer,
  resolveSmtpForEmail,
  SMTP_PRESETS,
} from '../../utils/emailProviderPresets';

interface SimpleForm {
  email: string;
  appPassword: string;
  fromName: string;
  smtpServer: string;
  smtpPort: number;
  enableSsl: boolean;
}

const emptyForm: SimpleForm = {
  email: '',
  appPassword: '',
  fromName: '',
  smtpServer: '',
  smtpPort: 587,
  enableSsl: true,
};

const CorreoSalida: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<TenantEmailSettingsStatus | null>(null);
  const [form, setForm] = useState<SimpleForm>(emptyForm);

  const detectedProvider = useMemo(() => detectProviderFromEmail(form.email), [form.email]);
  const isKnownProvider = detectedProvider !== 'custom';

  const providerHelp = useMemo(() => {
    if (detectedProvider === 'gmail') {
      return (
        <>
          En Google: Seguridad → Verificación en 2 pasos (activada) → Contraseñas de aplicaciones.
          {' '}
          <Link
            href="https://myaccount.google.com/apppasswords"
            target="_blank"
            rel="noopener noreferrer"
          >
            Crear contraseña de aplicación
          </Link>
        </>
      );
    }
    if (detectedProvider === 'outlook') {
      return (
        <>
          En Microsoft: Seguridad → Verificación en dos pasos → Contraseñas de aplicación.
          {' '}
          <Link
            href="https://account.microsoft.com/security"
            target="_blank"
            rel="noopener noreferrer"
          >
            Ir a seguridad de Microsoft
          </Link>
        </>
      );
    }
    return (
      <>
        Si su correo corporativo usa Google Workspace, el servidor suele ser <em>smtp.gmail.com</em>.
        Si usa Microsoft 365, suele ser <em>smtp.office365.com</em>. Consulte a su proveedor si no está seguro.
      </>
    );
  }, [detectedProvider]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await tenantEmailSettingsService.getStatus();
      setStatus(data);
      if (data.configurado) {
        const email = data.username ?? data.fromEmail ?? '';
        const providerFromEmail = detectProviderFromEmail(email);
        const provider =
          providerFromEmail !== 'custom'
            ? providerFromEmail
            : detectProviderFromSmtpServer(data.smtpServer);

        const preset =
          provider === 'gmail'
            ? SMTP_PRESETS.gmail
            : provider === 'outlook'
              ? SMTP_PRESETS.outlook
              : resolveSmtpForEmail(email, {
                  smtpServer: data.smtpServer ?? '',
                  smtpPort: data.smtpPort ?? 587,
                  enableSsl: data.enableSsl ?? true,
                });

        setForm({
          email,
          appPassword: '',
          fromName: data.fromName ?? '',
          smtpServer: preset.smtpServer,
          smtpPort: preset.smtpPort,
          enableSsl: preset.enableSsl,
        });
      }
    } catch (e) {
      console.error(e);
      enqueueSnackbar('Error al cargar la configuración de correo', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleEmailChange = (email: string) => {
    const smtp = resolveSmtpForEmail(email, {
      smtpServer: form.smtpServer,
      smtpPort: form.smtpPort,
      enableSsl: form.enableSsl,
    });
    setForm((prev) => ({
      ...prev,
      email,
      smtpServer: smtp.smtpServer,
      smtpPort: smtp.smtpPort,
      enableSsl: smtp.enableSsl,
    }));
  };

  const handleSave = async () => {
    const email = form.email.trim();
    if (!email) {
      enqueueSnackbar('Ingrese el correo electrónico', { variant: 'warning' });
      return;
    }
    if (!status?.tieneContrasena && !form.appPassword.trim()) {
      enqueueSnackbar('Ingrese la contraseña de aplicación', { variant: 'warning' });
      return;
    }

    const smtp = resolveSmtpForEmail(email, form);
    if (!smtp.smtpServer.trim()) {
      enqueueSnackbar('Complete el servidor SMTP en la configuración avanzada', { variant: 'warning' });
      return;
    }

    try {
      setSaving(true);
      const saved = await tenantEmailSettingsService.save({
        smtpServer: smtp.smtpServer.trim(),
        smtpPort: smtp.smtpPort,
        username: email,
        appPassword: form.appPassword.trim() || undefined,
        fromEmail: email,
        fromName: form.fromName.trim() || undefined,
        enableSsl: smtp.enableSsl,
      });
      setStatus(saved);
      setForm((prev) => ({ ...prev, appPassword: '' }));
      enqueueSnackbar('Correo configurado. Ya pueden enviar facturas por mail.', { variant: 'success' });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'No se pudo guardar la configuración';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('¿Eliminar la configuración de correo de esta empresa?')) return;
    try {
      setSaving(true);
      await tenantEmailSettingsService.remove();
      setStatus({ configurado: false, tieneContrasena: false });
      setForm(emptyForm);
      enqueueSnackbar('Configuración eliminada', { variant: 'info' });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'No se pudo eliminar la configuración';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2', mb: 1 }}>
            Correo para envío de facturas
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure una sola vez. Todos los usuarios de la empresa enviarán facturas desde esta cuenta.
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
              <CircularProgress size={24} />
              <Typography color="text.secondary">Cargando…</Typography>
            </Box>
          ) : (
            <Stack spacing={2.5} sx={{ maxWidth: 520 }}>
              {status?.configurado && (
                <Alert severity="success" variant="outlined">
                  Activo: las facturas se envían desde <strong>{status.fromEmail}</strong>
                </Alert>
              )}

              <TextField
                label="Correo electrónico"
                type="email"
                value={form.email}
                onChange={(e) => handleEmailChange(e.target.value)}
                fullWidth
                required
                placeholder="facturacion@miempresa.com"
                helperText="Detectamos Gmail u Outlook por el dominio del correo."
                disabled={saving}
              />

              {form.email.includes('@') && (
                <Box>
                  {isKnownProvider ? (
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={`Proveedor detectado: ${SMTP_PRESETS[detectedProvider].label}`}
                    />
                  ) : (
                    <Chip
                      size="small"
                      variant="outlined"
                      label="Correo corporativo u otro proveedor — complete SMTP abajo"
                    />
                  )}
                </Box>
              )}

              <TextField
                label="Contraseña de aplicación"
                type="password"
                value={form.appPassword}
                onChange={(e) => setForm((prev) => ({ ...prev, appPassword: e.target.value }))}
                fullWidth
                required={!status?.tieneContrasena}
                placeholder="xxxx xxxx xxxx xxxx"
                helperText={
                  status?.tieneContrasena
                    ? 'Deje en blanco para conservar la contraseña actual.'
                    : 'No use su contraseña habitual: genere una contraseña de aplicación.'
                }
                disabled={saving}
              />

              <TextField
                label="Nombre del remitente (opcional)"
                value={form.fromName}
                onChange={(e) => setForm((prev) => ({ ...prev, fromName: e.target.value }))}
                fullWidth
                placeholder="Mi Empresa S.A."
                helperText="Texto que verá el cliente junto al correo, por ejemplo el nombre de la empresa."
                disabled={saving}
              />

              <Accordion variant="outlined" disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2">¿Cómo obtengo la contraseña de aplicación?</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary">
                    {providerHelp}
                  </Typography>
                </AccordionDetails>
              </Accordion>

              {!isKnownProvider && form.email.includes('@') && (
                <Accordion variant="outlined" disableGutters defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2">Servidor SMTP</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      <TextField
                        label="Servidor SMTP"
                        value={form.smtpServer}
                        onChange={(e) => setForm((prev) => ({ ...prev, smtpServer: e.target.value }))}
                        fullWidth
                        required
                        placeholder="smtp.miempresa.com"
                        disabled={saving}
                      />
                      <TextField
                        label="Puerto"
                        type="number"
                        value={form.smtpPort}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            smtpPort: Number(e.target.value) || 587,
                          }))
                        }
                        fullWidth
                        required
                        disabled={saving}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={form.enableSsl}
                            onChange={(e) =>
                              setForm((prev) => ({ ...prev, enableSsl: e.target.checked }))
                            }
                            disabled={saving}
                          />
                        }
                        label="Usar SSL/TLS"
                      />
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pt: 1 }}>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                  {saving ? 'Guardando…' : status?.configurado ? 'Actualizar' : 'Guardar y activar'}
                </Button>
                {status?.configurado && (
                  <Button variant="outlined" color="error" onClick={handleRemove} disabled={saving}>
                    Quitar configuración
                  </Button>
                )}
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CorreoSalida;
